import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { catchError, Observable, of, Subject, takeUntil } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { SELECTABLE_LANGUAGES } from 'src/environments/runtime';

import { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY, UPDATE_LABELS } from '../../utils/constants';
import { TSettings } from '../appconfig/settings.model';
import { SettingsService } from '../settings/settings.service';
import { SocketService } from '../socket/socket.service';

@Injectable({
  providedIn: 'root',
})
export class TranslationManagerService implements OnDestroy {
  hdSettings$: Observable<Partial<TSettings>>;
  private destroy$ = new Subject<void>();
  language: string = DEFAULT_LANGUAGE;
  selectableLanguages = [];

  constructor(
    private settingsService: SettingsService,
    private translate: TranslateService,
    private socketService: SocketService,
    private http: HttpClient
  ) {
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
    try {
      this.selectableLanguages = SELECTABLE_LANGUAGES.split(',').map((s) => s.trim());
    } catch (error) {
      console.error(
        'Problem parsing selectable languages should be comma separated list:',
        SELECTABLE_LANGUAGES,
        error
      );
    }
    const storedLang = this.getStoredLanguage();
    const initialLang = storedLang ?? DEFAULT_LANGUAGE;
    this.setLanguage(initialLang);
  }

  private getStoredLanguage(): string | null {
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      return stored && this.selectableLanguages.includes(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  initSettings(): void {
    this.hdSettings$ = this.settingsService.getSettings;
    this.hdSettings$.pipe(takeUntil(this.destroy$)).subscribe(
      (settings: TSettings) => {
        const storedLanguage = this.getStoredLanguage();
        if (storedLanguage) {
          this.updateTranslations(storedLanguage);
          return;
        }
        const settingsLanguage = settings?.location?.language ?? '';
        const language = settingsLanguage.match(/en|fr|es|pl/)
          ? settingsLanguage
          : DEFAULT_LANGUAGE;
        this.updateTranslations(language);
      },
      (error) => {
        console.error(error);
        const storedLanguage = this.getStoredLanguage();
        this.updateTranslations(storedLanguage ?? DEFAULT_LANGUAGE);
      }
    );
  }

  getSelectableLanguages() {
    return this.selectableLanguages;
  }

  setLanguage(lang: string): void {
    this.language = lang;
    this.translate.use(lang);
    if (lang && this.selectableLanguages.includes(lang)) {
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      } catch (error) {
        console.warn('Storage not available', error);
      }
    }
  }

  listenForTranslationUpdates(): void {
    this.socketService.listen(UPDATE_LABELS, (data) => {
      this.translate.setTranslation(this.language, data.msg, true);
    });
  }

  updateTranslations(lang: string): void {
    this.setLanguage(lang);
    this.loadCustomStrings(lang).subscribe(
      (translations) => {
        this.translate.setTranslation(lang, translations, true);
      },
      (error) => {
        console.error(`Error loading custom-strings/${lang}.json:`, error);
      }
    );
  }

  loadCustomStrings(lang: string): Observable<any> {
    return this.http.get(`assets/custom-strings/${lang}.json`).pipe(
      catchError((error) => {
        console.error(`Error loading custom-strings/${lang}.json:`, error);
        return of({});
      })
    );
  }

  ngOnDestroy(): void {
    this.socketService.socket.off(UPDATE_LABELS);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
